/*
 * Such a weird name, but it's just a module for controls for a topic list
 */

define([
  "marionette",
  "underscore",
  "jquery",
  "messaging/messenger",
  "modules/topicsSplit/topics/topicsList/topicsControls/topicsControlsView",
  "modules/shared/moveModal/moveModalModule",
  "modules/topicsSplit/topics/topicsList/topicsControls/topicsControlsModel",
], function (
  Marionette,
  _,
  $,
  Messenger,
  TopicsControlsView,
  MoveModalModule,
  TopicsControlsModel,
) {
  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "topicsControls";
      this.region = options.region;
      this.showArchivedTopics = options.showArchivedTopics;
      this.showButtons = options.showButtons;
      this.showButtonsForArchived = options.showButtonsForArchived;
      this.namespace = options.namespace;
    },
    show: function (group, rootTopicGroup) {
      const kbId = Messenger.reqres.request("active:kb");
      this.group = group;
      const allowDeleteTopic = Messenger.reqres.request(
        "can:delete:topic",
        kbId,
        this.showButtonsForArchived,
      );
      group.allowEditTopicContext =
        Messenger.reqres.request("can:edit:topicContext") &&
        !this.showArchivedTopics &&
        !this.showButtonsForArchived &&
        this.showButtons;
      group.allowEditTopicFolder =
        Messenger.reqres.request("can:edit:topicFolder") &&
        !this.showArchivedTopics &&
        !this.showButtonsForArchived &&
        this.showButtons;
      const allowArchiveTopic = Messenger.reqres.request("can:archive:topic");
      group.allowEditTopicStatus =
        Messenger.reqres.request("can:edit:topicStatus") &&
        !this.showArchivedTopics &&
        !this.showButtonsForArchived &&
        this.showButtons;
      group.showArchiveTopicsButton =
        allowArchiveTopic &&
        !this.showArchivedTopics &&
        !this.showButtonsForArchived &&
        this.showButtons;
      if ((this.showArchivedTopics || this.showButtonsForArchived) && this.showButtons) {
        // non-admin should not be able to delete archived topics
        group.showRestoreTopicsButton = allowArchiveTopic;
        group.showDeleteTopicsButton = allowDeleteTopic;
      }
      group.showDeleteTopicsButton = allowDeleteTopic && this.showButtons;

      this.view = new TopicsControlsView.topicsControlsView({ model: group });
      if (group.isNew() && group.get("name").length === 0) {
        //auto-select group name textbox if the group is new
        this.view.on("show", () => this.view.enableTextbox("#topicFolderText"));
      }
      //show error message if the topic is found to be invalid
      group.on("invalid", (_model, error) => this.nameError(error["name"]), this);

      //delete all the topics that are selected
      this.view.on(
        "delete:topics",
        function () {
          //clear any old errors
          this.clearError();
          //get the checked models from the sortable list (with namespace)
          const topicsToDelete = this._getCheckedModels();
          if (topicsToDelete.length > 0) {
            Messenger.yesNoDialog(
              "topics.dialog.deleteTopics",
              {
                yes: function () {
                  Messenger.vent.trigger("track:interaction", {
                    action: "Delete Topics",
                    itemCount: topicsToDelete.length,
                    object: "Topic",
                  });
                  Messenger.vent.trigger("show:saving:display");
                  this._deleteTopics(topicsToDelete);
                },
              },
              this,
            );
          } else {
            this.controlsError($.t("topics.controls.emptyDelete"));
          }
        },
        this,
      );

      // archive selected topics
      this.view.on("archive:topics", this._archiveTopics, this);

      // restore selected topics
      this.view.on("restore:topics", this._restoreTopics, this);

      //move all the topics that are selected
      this.view.on(
        "move:topics",
        function () {
          //clear any old errors
          this.clearError();
          //get the checked models from the sortable list (with namespace)
          const topicsToMove = this._getCheckedModels();
          if (topicsToMove.length > 0) {
            Messenger.save(function (success) {
              //save before attempting the move
              if (success) {
                Messenger.vent.trigger(
                  "show:modal",
                  MoveModalModule,
                  {},
                  {
                    modelsToMove: topicsToMove,
                    rootGroup: rootTopicGroup,
                    nameAttribute: "name",
                    group: group,
                  },
                  {
                    title: $.t("topics.move.title"),
                    modalWidth: 400,
                    trackInteraction: {
                      action: "Move Topic to Folder",
                      object: "Topic",
                    },
                  },
                );
              }
            }, this);
          } else {
            this.controlsError($.t("topics.controls.emptyMove"));
          }
        },
        this,
      );
      this.view.on("open:topic:context:menu", this._onOpenTopicContextMenu, this);
      //expand 'change status' menu and populate the options (which statuses can we navigate to?)
      this.view.on(
        "open:topic:status:menu",
        function () {
          //clear any old errors
          this.clearError();
          let selectedTopics = this._getCheckedModels();
          if (selectedTopics.length > 0) {
            const statuses = Messenger.reqres.request("kb:statuses");
            statuses.onSynced(function () {
              //iterate through each topic, and find the *union* of all their possible transitions
              const transitions = this._getStatusTransitions(statuses, selectedTopics);
              this.view.setStatusTransitionMenu(transitions, selectedTopics);
            }, this);
          } else {
            this.controlsError($.t("topics.controls.emptyChangeStatus"));
          }
        },
        this,
      );
      this.view.on("change:topic:context", this._onChangeTopicContext, this);
      //send request to update an array of topics
      this.view.on(
        "change:topic:status",
        function (topics, newStatusId) {
          //get the status name from the id
          let statusName;
          const statuses = Messenger.reqres.request("kb:statuses"); //already synced
          const statusArray = statuses.get("allTopicStatuses");
          for (let i = 0; i < statusArray.length; i++) {
            if (statusArray[i].statusId === newStatusId) {
              statusName = statusArray[i].statusName;
              break;
            }
          }
          let topicIds = []; //array of topic ids to send to the server
          _.each(
            topics,
            function (topic) {
              if (topic.get("statusId") !== newStatusId) {
                //only change topics with different statuses
                topicIds.push(topic.get("intentId"));
              }
            },
            this,
          );
          //save the new statuses
          let request = new TopicsControlsModel.multiTopicStatusModel({
            statusId: newStatusId,
            intentIds: topicIds,
          });
          //show the save display while the server is processing the updates
          Messenger.vent.trigger("show:saving:display");
          request.save(null, {
            success: function (model, response) {
              //apply status id and name to each topic that had status changed successfully by the server.
              const changedIntentIds = response.intentIds;
              _.each(
                topics,
                function (topic) {
                  if (changedIntentIds.includes(topic.get("intentId"))) {
                    // only change the status of topics that were actually saved by the server.
                    topic.set("statusId", newStatusId, { silent: true }); //do this silently to avoid the topic thinking it needs to publish anything
                    topic.statusValue = statusName; //as a result of silencing the above 'set', we need to manually set the status value
                    topic.trigger("change"); //helps update the sortable list
                    topic.dirty = false; //triggering the change sets the dirty flag to true, but the models are not actually dirty
                  }
                },
                this,
              );
              //uncheck all of the checkboxes
              Messenger.vent.trigger(
                Messenger.getFullName("toggle:all:checkboxes", this.namespace),
                false,
              );
              //close save display
              Messenger.vent.trigger("close:saving:display");
            },
            error: function (model, response) {
              //close save display
              Messenger.vent.trigger("close:saving:display");
              Messenger.showErrorFromResponse(response);
            },
          });
        },
        this,
      );
      //add sub folder to the current folder
      this.view.on(
        "add:folder",
        function () {
          //check if we can close and save any open textboxes
          if (Messenger.reqres.request("save:textbox")) {
            //this check is not sufficient.  We needs to also check if the topic group is valid
            if (group.isValid()) {
              console.log("add folder");
              //need to get sub groups from the server first
              if (group.get("hasSubgroups")) {
                group.trigger("expand"); //expand group and load/show any subgroups
                //once the subGroups are synced, add a new group
                group.subGroups().onSynced(function () {
                  console.log("select group");
                  const newGroup = group.addSubGroup();
                  newGroup.trigger("select"); //select the new group for editing
                }, this);
              } else {
                const newGroup = group.addSubGroup();
                group.trigger("expand");
                newGroup.trigger("select");
              }
            }
          }
        },
        this,
      );
      //move folder
      this.view.on(
        "move:folder",
        function () {
          if (Messenger.reqres.request("save:textbox")) {
            if (group.isValid()) {
              console.log("move folder");
              //remove the folder from the collection, and show the move modal
              group.parent = group.collection.parent; //save parent just in case we need to cancel move
              group.collection.remove(group);
              Messenger.vent.trigger(
                "show:modal",
                MoveModalModule,
                {},
                {
                  rootGroup: rootTopicGroup,
                  nameAttribute: "name",
                  group: group,
                },
                {
                  title: $.t("topics.moveFolder.title"),
                  modalWidth: 400,
                  trackInteraction: {
                    action: "Move Topic Folder",
                    object: "Topic",
                  },
                },
              );
            }
          }
        },
        this,
      );
      //delete current folder
      this.view.on(
        "delete:folder",
        function () {
          console.log("delete folder");
          group.topics().onSynced(function () {
            if (group.topics().length === 0 && !group.get("hasSubgroups")) {
              Messenger.yesNoDialog(
                "topics.dialog.deleteFolder",
                {
                  yes: function () {
                    Messenger.vent.trigger("track:interaction", {
                      action: "Delete Topic Folder",
                      id: group.id,
                      idType: "Topic Folder",
                      object: "Topic Folder",
                    });
                    const parentModel = group.collection.parent;
                    Messenger.vent.trigger("cancel:textbox"); //cancel any open textboxes
                    group.deleteModel();
                    //switch to parent folder
                    parentModel.trigger("select");
                  },
                },
                this,
              );
            } else {
              Messenger.okDialog("topics.dialog.nonEmptyFolder");
            }
          }, this);
        },
        this,
      );
      this.showRegion(this.view);
    },
    clearError: function () {
      this.view.clearError();
    },
    controlsError: function (errorMessage) {
      this.view.controlsError(errorMessage);
    },
    nameError: function (errorMessage) {
      this.view.nameError(errorMessage);
    },
    _getCheckedModels: function () {
      return Messenger.reqres.request(Messenger.getFullName("checked:models", this.namespace));
    },
    _archiveTopics: function () {
      //clear any old errors
      this.clearError();
      //get the checked models from the sortable list (with namespace)
      const topicsToArchive = this._getCheckedModels();
      if (topicsToArchive.length > 0) {
        Messenger.yesNoDialog(
          "topics.dialog.archiveTopics",
          {
            yes: () => {
              Messenger.vent.trigger("show:saving:display");
              Messenger.vent.trigger("track:interaction", {
                action: "Archive Topics",
                itemCount: topicsToArchive.length,
                object: "Topic",
              });

              const topicIdsToArchive = topicsToArchive.map((topic) => topic.get("intentId"));
              let request = new TopicsControlsModel.topicsArchiveModel({
                intent_Ids: topicIdsToArchive,
              });
              request.save(null, {
                success: (model, response) => {
                  const changedIntentIds = response.intent_Ids;
                  const archivedTopics = [];
                  changedIntentIds.forEach((changedIntentId) => {
                    const topic = topicsToArchive.find(
                      (topic) => topic.get("intentId") === changedIntentId,
                    );
                    if (topic) {
                      topic.set("archived", true, { silent: true }); //do this silently to avoid the topic thinking it needs to save anything
                      archivedTopics.push(topic);
                    }
                  });
                  if (archivedTopics.length > 0) {
                    this.group.updateArchivedTopics(archivedTopics);
                  }
                  Messenger.vent.trigger("close:saving:display");
                },
                error: (model, response) => {
                  Messenger.vent.trigger("close:saving:display");
                  Messenger.showErrorFromResponse(response);
                },
              });
            },
          },
          this,
        );
      } else {
        this.controlsError($.t("topics.controls.emptyArchive"));
      }
    },
    _onChangeTopicContext: function (topics, newContextId) {
      let topicIds = []; //array of topic ids to send to the server
      topics.forEach((topic) => {
        if (topic.get("contextId") !== newContextId) {
          //only change topics with different contexts
          topicIds.push(topic.get("intentId"));
        }
      }, this);
      //save the new contexts
      let request = new TopicsControlsModel.multiTopicContextModel({
        contextId: newContextId,
        intentIds: topicIds,
      });
      //show the save display while the server is processing the updates
      Messenger.vent.trigger("show:saving:display");
      request.save(null, {
        success: function (_model, response) {
          //apply context id and value to each topic that had context changed successfully by the server.
          const changedIntentIds = response.intentIds;
          const newContextValue = response.contextValue;
          topics.forEach((topic) => {
            if (changedIntentIds.includes(topic.get("intentId"))) {
              // only change the context of topics that were actually saved by the server.
              topic.set("contextId", newContextId, { silent: true }); //do this silently to avoid the topic thinking it needs to publish anything
              topic.contextValue = newContextValue; //as a result of silencing the above 'set', we need to manually set the context value
              topic.trigger("change"); //helps update the sortable list
              topic.dirty = false; //triggering the change sets the dirty flag to true, but the models are not actually dirty
            }
          });
          //uncheck all the checkboxes
          Messenger.vent.trigger(
            Messenger.getFullName("toggle:all:checkboxes", this.namespace),
            false,
          );
          //close save display
          Messenger.vent.trigger("close:saving:display");
        },
        error: function (_model, response) {
          //close save display
          Messenger.vent.trigger("close:saving:display");
          Messenger.showErrorFromResponse(response);
        },
      });
    },
    _onOpenTopicContextMenu: function () {
      //clear any old errors
      this.clearError();
      let selectedTopics = this._getCheckedModels();
      if (selectedTopics.length > 0) {
        const contexts = Messenger.reqres.request("kb:contexts");
        contexts.onSynced(() => this.view.setTopicContextMenu(contexts, selectedTopics));
      } else {
        this.controlsError($.t("topics.controls.emptyChangeContext"));
      }
    },
    _restoreTopics: function () {
      // clear any old errors
      this.clearError();
      // get the checked models from the sortable list (with namespace)
      const topicsToRestore = this._getCheckedModels();
      if (topicsToRestore.length > 0) {
        Messenger.yesNoDialog(
          "topics.dialog.restoreTopics",
          {
            yes: () => {
              Messenger.vent.trigger("show:saving:display");
              Messenger.vent.trigger("track:interaction", {
                action: "Restore Topics",
                itemCount: topicsToRestore.length,
                object: "Topic",
              });

              const topicIdsToRestore = topicsToRestore.map((topic) => topic.get("intentId"));
              let request = new TopicsControlsModel.topicsRestoreModel({
                intent_Ids: topicIdsToRestore,
              });
              request.save(null, {
                success: (model, response) => {
                  const changedIntentIds = response.intent_Ids;
                  const restoredTopics = [];
                  changedIntentIds.forEach((changedIntentId) => {
                    const topic = topicsToRestore.find(
                      (topic) => topic.get("intentId") === changedIntentId,
                    );
                    if (topic) {
                      topic.set("archived", false, { silent: true }); //do this silently to avoid the topic thinking it needs to save anything
                      restoredTopics.push(topic);
                    }
                  });
                  if (restoredTopics.length > 0) {
                    this.group.updateRestoredTopics(restoredTopics);
                  }
                  Messenger.vent.trigger("close:saving:display");
                },
                error: (model, response) => {
                  Messenger.vent.trigger("close:saving:display");
                  Messenger.showErrorFromResponse(response);
                },
              });
            },
          },
          this,
        );
      } else {
        this.controlsError($.t("topics.controls.emptyRestore"));
      }
    },
    _deleteTopics: function (topicsToDelete) {
      if (!topicsToDelete || !topicsToDelete.length) {
        Messenger.vent.trigger("close:saving:display");
        return;
      }
      const topicToDelete = topicsToDelete.pop();
      topicToDelete.deleteModel({
        success: () => {
          this._deleteTopics(topicsToDelete);
        },
        error: (model, response) => {
          Messenger.vent.trigger("close:saving:display");
          Messenger.showErrorFromResponse(response);
        },
      });
    },
    _getStatusTransitions: function (statuses, topics) {
      //get transitions from status object (object is assumed to be synced, so call function accordingly)
      //filter out all "Publish..."/"Disable..."/"Enable..." transitions because we only want be to able to
      // "Publish All"/"Disable All"/"Enable All"
      const selectedRegEx = /^.*Selected$/;
      const statusTransitions = _.filter(
        statuses.get("allTopicStatusTransitions"),
        function (transition) {
          return !selectedRegEx.test(transition);
        },
      );
      let allTransitions = {};
      //loop through each topic and collect transitions to unique target statuses (we are essentially performing a union on target status Id)
      _.each(
        topics,
        function (topic) {
          for (let i = 0; i < statusTransitions.length; i++) {
            let transition = statusTransitions[i];
            if (transition.sourceStatusId === topic.get("statusId")) {
              allTransitions[transition.targetStatusId] = transition;
            }
          }
        },
        this,
      );
      allTransitions = _.values(allTransitions); //convert object to list of transitions

      return allTransitions;
    },
  });
});
